import { z } from "zod"

import type { Location } from "@/graphql/codegen/graphql"
import { LocationUpdateInputSchema } from "@/graphql/codegen/graphql"
import type { DialogCloseAction } from "@/types/common"
import { stripCountryCodeAndSpacesFromPhoneNumber } from "@/utils/utils"

// ===== Location Update and Create Schemas =====

const LocationUpdateSchema = LocationUpdateInputSchema()

export const AS400PreviewSchema = z.object({
  address1: z.string().min(1, "Address is required"),
  address2: z.string(),
  city: z.string().min(1, "City is required"),
  name: z.string().min(1, "Name is required"),
  regionIsoCode: z.string().optional(),
  postalCode: z.string().optional(),
})

export type AS400PreviewFormValues = z.infer<typeof AS400PreviewSchema>

export type AerosParentFields = keyof Pick<
  AddressFields,
  "name" | "address1" | "address2" | "city" | "regionIsoCode" | "postalCode"
>

const BaseAddressSchema = z
  .object({
    address1: z.string().min(1, "Address is required"),
    address2: z.string().optional().default(""),
    city: z.string().min(1, "City is required"),
    countryIsoCode: z.string().min(1, "Country is required"),
    regionIsoCode: z.string().optional().nullish(),
    postalCode: z.string().optional().nullish(),
    name: z.string().min(1, "Name is required"),
    description: z.string().min(1, "Description is required"),
  })
  .extend({
    as400: AS400PreviewSchema,
  })

const AccountManagerSchema = z.object({
  accountManager: z
    .object({
      id: z.string(),
      name: z.string(),
    })
    .optional()
    .nullable(),
})

const salesmanSchema = z.object({
  salesman: z
    .object({
      value: z.string(),
      label: z.string(),
    })
    .optional()
    .nullable(),
})

export const AddressCreateSchema = BaseAddressSchema.merge(AccountManagerSchema).merge(salesmanSchema)

export const AddressUpdateSchema = LocationUpdateSchema.pick({
  address1: true,
  address2: true,
  city: true,
  countryIsoCode: true,
  regionIsoCode: true,
  postalCode: true,
  name: true,
  description: true,
}).merge(BaseAddressSchema)

const ContactSchema = LocationUpdateSchema.pick({
  phoneNumber: true,
  altPhoneNumber: true,
  faxNumber: true,
}).extend({
  phoneNumber: z
    .string()
    .nullish()
    .refine(
      (val) => {
        if (!val) return true
        const strippedPhoneNumber = stripCountryCodeAndSpacesFromPhoneNumber(val)
        return strippedPhoneNumber?.length === 10
      },
      { message: "Phone number must have exactly 10 digits" }
    ),
  altPhoneNumber: z.string().nullish(),
  faxNumber: z.string().nullish(),
})

const NotesSchema = LocationUpdateSchema.pick({
  notes: true,
})

// ===== Shared Types ====

export type AddressFields = z.infer<typeof AddressUpdateSchema>
export type ContactFields = z.infer<typeof ContactSchema>
export type NotesSchema = z.infer<typeof NotesSchema>

// ===== Location Update Types =====

export interface BaseDialogProps<T, K> {
  data: T
  isCustomerCompanyType?: boolean
  onSubmit: (data: K) => void
  onClose: ({ data, reason }: { data: T | Record<string, any>; reason: DialogCloseAction }) => void
}

export type EditDialogProps<T, K> = BaseDialogProps<T, K> & {
  activeEditLocation: Location | undefined
}

// Schema and types for editing location
export const EditAddressQuerySchema = AddressUpdateSchema
export type EditAddressQueryProps = z.infer<typeof EditAddressQuerySchema>
export type EditAddressDialogProps = EditDialogProps<AddressFields, EditAddressQueryProps>

// Schema and types for editing contact
export const EditContactQuerySchema = ContactSchema
export type EditContactQueryProps = z.infer<typeof EditContactQuerySchema>
export type EditContactDialogProps = EditDialogProps<ContactFields, EditContactQueryProps>

// Schema and types for editing notes
export const EditNotesQuerySchema = NotesSchema
export type EditNotesQueryProps = z.infer<typeof EditNotesQuerySchema>
export type EditNotesDialogProps = EditDialogProps<NotesSchema, EditNotesQueryProps>

// Schema and types for editing account manager
export const UpdateAccountManagerSchema = AccountManagerSchema
export type UpdateAccountManagerProps = z.infer<typeof UpdateAccountManagerSchema>
export type UpdateAccountManagerDialogProps = EditDialogProps<
  z.infer<typeof AccountManagerSchema>,
  UpdateAccountManagerProps
>

// Schema and types for editing salesman
export const UpdateSalesmanSchema = salesmanSchema
export type UpdateSalesmanProps = z.infer<typeof UpdateSalesmanSchema>
export type UpdateSalesmanDialogProps = EditDialogProps<z.infer<typeof salesmanSchema>, UpdateSalesmanProps>

// ===== Location Create Types =====

// Schema and types for adding address
export type AddAddressQueryProps = z.infer<typeof AddressCreateSchema>
export type LocationCreateFormValue = z.infer<typeof AddressCreateSchema>
export type AddAddressDialogProps = BaseDialogProps<AddressFields, AddAddressQueryProps>

// Schema and types for adding notes
export const AddNotesQuerySchema = NotesSchema
export type AddNotesQueryProps = z.infer<typeof AddNotesQuerySchema>
export type AddNotesDialogProps = BaseDialogProps<NotesSchema, AddNotesQueryProps>
